import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { navigate } from 'gatsby'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	Stitch,
	RemoteMongoClient,
	UserApiKeyCredential,
} from 'mongodb-stitch-browser-sdk'
import DatePicker from 'react-datepicker'
import moment from 'moment'

import MySelect from './MySelect'

const FormSchema = Yup.object().shape({
	firstName: Yup.string()
		.min(2, 'Too Short!')
		.max(50, 'Too Long!')
		.required('Required'),
	lastName: Yup.string()
		.min(2, 'Too Short!')
		.max(50, 'Too Long!')
		.required('Required'),
	email: Yup.string()
		.email('Invalid email')
		.required('Required'),
	phone: Yup.string()
		.matches(
			/(^1(3|8)[0-9]{2}( |-){0,1}[0-9]{3}( |-){0,1}[0-9]{3})|(^\({0,1}(0|\+61){0,1}( |-){0,1}0{0,1}(2|4|3|7|8){0,1}\){0,1}( |-){0,1}[0-9]{2}( |-){0,1}[0-9]{2}( |-){0,1}[0-9]{1}( |-){0,1}[0-9]{3})|(^13(( |-){0,1}[0-9]{2}( |-){0,1}[0-9]{2}))|(^13[0-9]( |-){0,1}[0-9]{3})/,
			'Invalid phone number'
		)
		.required('Required'),
	date: Yup.string().required('Required'),
	street: Yup.string()
		.min(2, 'Too Short!')
		.max(50, 'Too Long!')
		.required('Required'),
	city: Yup.string()
		.min(2, 'Too Short!')
		.max(50, 'Too Long!')
		.required('Required'),
	postcode: Yup.string()
		.matches(
			/^(((2|8|9)\d{2})|((02|08|09)\d{2})|([1-9]\d{3}))$/,
			'Invalid Postcode'
		)
		.required('Required'),
	region: Yup.string().required('Required'),
})

function validationIcons(valid) {
	return valid ? (
		<span className="icon is-small is-right">
			<FontAwesomeIcon icon="exclamation-triangle" />
		</span>
	) : (
		<span className="icon is-small is-right">
			<FontAwesomeIcon icon="check" />
		</span>
	)
}

class BookADemo extends Component {
	constructor(props) {
		super(props)
		this.state = {}
	}

	componentDidMount() {
		if (!Stitch.hasAppClient('book-a-demo-hcaug')) {
			this.client = Stitch.initializeDefaultAppClient('book-a-demo-hcaug')
		} else {
			this.client = Stitch.defaultAppClient
		}
		this.db = this.client
			.getServiceClient(RemoteMongoClient.factory, 'mongodb-atlas')
			.db('bookings')
	}

	render() {
		const {
			formName,
			modelOptions,
			regionOptions,
			conditionOptions,
		} = this.props
		return (
			<Formik
				initialValues={{
					firstName: '',
					lastName: '',
					email: '',
					phone: '',
					date: '',
					street: '',
					city: '',
					postcode: '',
					region: '',
					model: '',
					poolType: '',
					pool_condition: '',
					poolSize: '',
				}}
				validationSchema={FormSchema}
				onSubmit={(values, { setSubmitting, setStatus, resetForm }) => {
					setSubmitting(true)
					const robots = values.model
						? values.model.map(model => model.value)
						: ''
					const condition = values.pool_condition
						? values.pool_condition.value
						: ''
					const fullAddress = `${values.street}, ${values.city} ${
						values.region.value
					} ${values.postcode}`
					this.client.auth
						.loginWithCredential(
							new UserApiKeyCredential(process.env.GATSBY_MONGODB)
						)
						.then(() => {
							moment.fn.addWorkdays = function(days) {
								const increment = days / Math.abs(days)
								const date = this.clone().add(
									Math.floor(Math.abs(days) / 6) * 7 * increment,
									'days'
								)
								let remaining = days % 6
								while (remaining !== 0) {
									date.add(increment, 'days')
									if (date.isoWeekday() !== 7) remaining -= increment
								}
								return date
							}
							const today = moment()
							this.client
								.callFunction('newLead', [
									{
										firstName: values.firstName,
										lastName: values.lastName,
										email: values.email,
										phone: values.phone,
										dateTime: moment(values.date).format('LLLL'),
										address: fullAddress,
										postcode: values.postcode,
										poolType: values.poolType,
										poolState: condition,
										poolSize: values.poolSize,
										models: robots,
										leadCreated: today.format('LLLL'),
										leadDue: today.addWorkdays(3).format('LLLL'),
									},
								])
								.then(() => {
									setSubmitting(false)
									resetForm()
									navigate('?form=book-a-demo')
									setStatus('success')
								})
								.catch(err => {
									console.error(`MongoDB Stitch fn() Error:\n`, err.message)
									setSubmitting(false)
									setStatus('failure')
								})
						})
						.catch(err => {
							console.error(`MongoDB API Auth is bad:\n`, err.message)
							setSubmitting(false)
							setStatus('failure')
						})
				}}
				render={({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					handleSubmit,
					isSubmitting,
					setStatus,
					status,
					setFieldValue,
					setFieldTouched,
				}) => (
					<form
						name={formName}
						method="post"
						data-netlify="true"
						data-netlify-honeypot="bot-field"
						onSubmit={handleSubmit}
						className="form"
					>
						<input type="hidden" name="form-name" defaultValue="book-a-demo" />
						<div className="field is-horizontal">
							<div className="field-label">
								<label className="label" htmlFor="name">
									Full Name
								</label>
							</div>
							<div className="field-body">
								<div className="field is-expanded" aria-describedby="firstName">
									<div className="control has-icons-left has-icons-right">
										<input
											id="firstName"
											name="firstName"
											type="text"
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.firstName}
											placeholder="First Name"
											className={classNames(
												touched.firstName && errors.firstName && `is-danger`,
												values.firstName && !errors.firstName && `is-success`,
												`input`
											)}
											required={true}
										/>
										<span className="icon is-small is-left">
											<FontAwesomeIcon icon="user" />
										</span>
										{touched.firstName &&
											errors.firstName &&
											validationIcons(true)}
										{values.firstName &&
											!errors.firstName &&
											validationIcons(false)}
									</div>
									{touched.firstName && errors.firstName && (
										<p className="help is-danger" id="firstName-helper-text">
											{errors.firstName}
										</p>
									)}
								</div>
								<div className="field is-expanded" aria-describedby="lastName">
									<div className="control has-icons-left has-icons-right">
										<input
											id="lastName"
											name="lastName"
											type="text"
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.lastName}
											placeholder="Last Name"
											className={classNames(
												touched.lastName && errors.lastName && `is-danger`,
												values.lastName && !errors.lastName && `is-success`,
												`input`
											)}
											required={true}
										/>
										<span className="icon is-small is-left">
											<FontAwesomeIcon icon="user" />
										</span>
										{touched.lastName &&
											errors.lastName &&
											validationIcons(true)}
										{values.lastName &&
											!errors.lastName &&
											validationIcons(false)}
									</div>
									{touched.lastName && errors.lastName && (
										<p className="help is-danger" id="last-name-helper-text">
											{errors.lastName}
										</p>
									)}
								</div>
							</div>
						</div>
						<div className="field is-horizontal">
							<div className="field-label">
								<label className="label" htmlFor="email">
									Email
								</label>
							</div>
							<div className="field-body">
								<div className="field is-expanded" aria-describedby="email">
									<div className="control has-icons-left has-icons-right">
										<input
											id="email"
											name="email"
											type="email"
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.email}
											placeholder="you@example.com"
											className={classNames(
												touched.email && errors.email && `is-danger`,
												values.email && !errors.email && `is-success`,
												`input`
											)}
											required={true}
										/>
										<span className="icon is-small is-left">
											<FontAwesomeIcon icon="envelope" />
										</span>
										{touched.email && errors.email && validationIcons(true)}
										{values.email && !errors.email && validationIcons(false)}
									</div>
									{touched.email && errors.email && (
										<p className="help is-danger" id="email-helper-text">
											{errors.email}
										</p>
									)}
								</div>
							</div>
						</div>
						<div className="field is-horizontal">
							<div className="field-label">
								<label className="label" htmlFor="phone">
									Phone
								</label>
							</div>
							<div className="field-body">
								<div className="field is-expanded" aria-describedby="phone">
									<div className="control has-icons-left has-icons-right">
										<input
											id="phone"
											name="phone"
											type="tel"
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.phone}
											placeholder="Phone Number"
											className={classNames(
												touched.phone && errors.phone && `is-danger`,
												values.phone && !errors.phone && `is-success`,
												`input`
											)}
											required={true}
										/>
										<span className="icon is-small is-left">
											<FontAwesomeIcon icon="phone" />
										</span>
										{touched.phone && errors.phone && validationIcons(true)}
										{values.phone && !errors.phone && validationIcons(false)}
									</div>
									{touched.phone && errors.phone && (
										<p className="help is-danger" id="phone-helper-text">
											{errors.phone}
										</p>
									)}
								</div>
							</div>
						</div>
						<div className="field is-horizontal">
							<div className="field-label">
								<label className="label" htmlFor="date">
									Preferred Time
								</label>
							</div>
							<div className="field-body">
								<div className="field is-expanded" aria-describedby="date">
									<div className="control has-icons-left has-icons-right">
										<DatePicker
											id="booking-date"
											type="date"
											selected={values.date ? moment(values.date) : null}
											onChange={date => setFieldValue('date', date)}
											onBlur={blur => setFieldTouched('date', blur)}
											showTimeSelect
											minTime={moment()
												.hours(8)
												.minutes(0)}
											maxTime={moment()
												.hours(17)
												.minutes(0)}
											minDate={moment()}
											maxDate={moment().add(2, 'months')}
											timeIntervals={120}
											filterDate={date => {
												const day = date.day()
												return day !== 0
											}}
											showDisabledMonthNavigation
											utcOffset={+10}
											locale="en-au"
											dateFormat="DD/MM/YYYY, h:mma"
											className={classNames(
												touched.date && errors.date && `is-danger`,
												values.date && !errors.date && `is-success`,
												`input`
											)}
											placeholderText="Booking Date and Time"
											required={true}
										/>
										<span className="icon is-small is-left">
											<FontAwesomeIcon icon="calendar-alt" />
										</span>
										{touched.date && errors.date && validationIcons(true)}
										{values.date && !errors.date && validationIcons(false)}
									</div>
									{touched.date && errors.date && (
										<p className="help is-danger" id="date-helper-text">
											{errors.date}
										</p>
									)}
								</div>
							</div>
						</div>
						<div className="field is-horizontal">
							<div className="field-label">
								<label className="label">Address</label>
							</div>
							<div className="field-body">
								<div className="field street" aria-describedby="street">
									<label className="label is-small" htmlFor="street">
										Street
									</label>
									<div className="control has-icons-left has-icons-right">
										<input
											id="street"
											name="street"
											type="text"
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.street}
											placeholder="Street Name"
											className={classNames(
												touched.street && errors.street && `is-danger`,
												values.street && !errors.street && `is-success`,
												`input`
											)}
											required={true}
										/>
										<span className="icon is-small is-left">
											<FontAwesomeIcon icon="map-marked-alt" />
										</span>
										{touched.street && errors.street && validationIcons(true)}
										{values.street && !errors.street && validationIcons(false)}
									</div>
									{touched.street && errors.street && (
										<p className="help is-danger" id="street-helper-text">
											{errors.street}
										</p>
									)}
								</div>
							</div>
						</div>
						<div className="field is-horizontal">
							<div className="field-label" />
							<div className="field-body">
								<div className="field suburb" aria-describedby="city">
									<label className="label is-small" htmlFor="city">
										City
									</label>
									<div className="control has-icons-left has-icons-right">
										<input
											id="city"
											name="city"
											type="text"
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.city}
											placeholder="City Name"
											className={classNames(
												touched.city && errors.city && `is-danger`,
												values.city && !errors.city && `is-success`,
												`input`
											)}
											required={true}
										/>
										<span className="icon is-small is-left">
											<FontAwesomeIcon icon="map-marked-alt" />
										</span>
										{touched.city && errors.city && validationIcons(true)}
										{values.city && !errors.city && validationIcons(false)}
									</div>
									{touched.city && errors.city && (
										<p className="help is-danger" id="city-helper-text">
											{errors.city}
										</p>
									)}
								</div>
								<div className="field region" aria-describedby="region">
									<label className="label is-small" htmlFor="state">
										State/Territory
									</label>
									<div className="control">
										<MySelect
											identity={'region'}
											icon={'map-marked-alt'}
											placeholder={'State'}
											options={regionOptions}
											value={values.region}
											onChange={setFieldValue}
											onBlur={setFieldTouched}
											errors={errors.region}
											touched={touched.region}
											multi={false}
											className={'region-select-container'}
										/>
									</div>
								</div>
								<div className="field postcode" aria-describedby="postcode">
									<label className="label is-small" htmlFor="postcode">
										Postcode
									</label>
									<div className="control has-icons-left has-icons-right">
										<input
											id="postcode"
											name="postcode"
											type="tel"
											onChange={handleChange}
											onBlur={handleBlur}
											value={values.postcode}
											placeholder="Postcode"
											className={classNames(
												touched.postcode && errors.postcode && `is-danger`,
												values.postcode && !errors.postcode && `is-success`,
												`input`
											)}
											required={true}
										/>
										<span className="icon is-small is-left">
											<FontAwesomeIcon icon="map-marked-alt" />
										</span>
										{touched.postcode &&
											errors.postcode &&
											validationIcons(true)}
										{values.postcode &&
											!errors.postcode &&
											validationIcons(false)}
									</div>
									{touched.postcode && errors.postcode && (
										<p className="help is-danger" id="postcode-helper-text">
											{errors.postcode}
										</p>
									)}
								</div>
							</div>
						</div>
						{values.postcode ? (
							<div>
								<div className="field is-horizontal">
									<div className="field-label">
										<label className="label">Models</label>
									</div>
									<div className="field-body">
										<div className="field" aria-describedby="model-select">
											<div className="control">
												<MySelect
													identity={'model'}
													icon={'robot'}
													placeholder={'Models'}
													options={modelOptions}
													value={values.model}
													onChange={setFieldValue}
													onBlur={setFieldTouched}
													errors={errors.model}
													touched={touched.model}
													multi={true}
													className={'region-select-container'}
												/>
											</div>
										</div>
									</div>
								</div>
								<div className="field is-horizontal">
									<div className="field-label">
										<label className="label">Pool Condition</label>
									</div>
									<div className="field-body">
										<div
											className="field"
											aria-describedby="pool_condition-select"
										>
											<div className="control">
												<MySelect
													identity={'pool_condition'}
													icon={'flask'}
													placeholder={'Condition'}
													options={conditionOptions}
													value={values.pool_condition}
													onChange={setFieldValue}
													onBlur={setFieldTouched}
													errors={errors.pool_condition}
													touched={touched.pool_condition}
													multi={false}
													className={'region-select-container'}
												/>
											</div>
										</div>
									</div>
								</div>
								<div className="field is-horizontal">
									<div className="field-label">
										<label className="label" htmlFor="pool-size">
											Pool Size
										</label>
									</div>
									<div className="field-body">
										<div
											className="field is-expanded"
											aria-describedby="pool-size"
										>
											<div className="control has-icons-left has-icons-right">
												<input
													id="pool-size"
													name="poolSize"
													type="text"
													onChange={handleChange}
													onBlur={handleBlur}
													value={values.poolSize}
													placeholder="25000L"
													className={classNames(
														touched.poolSize && errors.poolSize && `is-danger`,
														values.poolSize && !errors.poolSize && `is-success`,
														`input`
													)}
												/>
												<span className="icon is-small is-left">
													<FontAwesomeIcon icon="cube" />
												</span>
												{touched.poolSize &&
													errors.poolSize &&
													validationIcons(true)}
												{values.poolSize &&
													!errors.poolSize &&
													validationIcons(false)}
											</div>
											{touched.poolSize && errors.poolSize && (
												<p
													className="help is-danger"
													id="pool-size-helper-text"
												>
													{errors.poolSize}
												</p>
											)}
										</div>
									</div>
								</div>
								<div className="field is-horizontal">
									<div className="field-label">
										<label className="label" htmlFor="pool-surface">
											Pool Surface
										</label>
									</div>
									<div className="field-body">
										<div
											className="field is-expanded"
											aria-describedby="pool-surface"
										>
											<div className="control has-icons-left has-icons-right">
												<input
													id="pool-surface"
													name="poolType"
													type="text"
													onChange={handleChange}
													onBlur={handleBlur}
													value={values.poolType}
													placeholder="Tiles, Pebbles, Concrete"
													className={classNames(
														touched.poolType && errors.poolType && `is-danger`,
														values.poolType && !errors.poolType && `is-success`,
														`input`
													)}
												/>
												<span className="icon is-small is-left">
													<FontAwesomeIcon icon="swimming-pool" />
												</span>
												{touched.poolType &&
													errors.poolType &&
													validationIcons(true)}
												{values.poolType &&
													!errors.poolType &&
													validationIcons(false)}
											</div>
											{touched.poolType && errors.poolType && (
												<p
													className="help is-danger"
													id="pool-surface-helper-text"
												>
													{errors.poolType}
												</p>
											)}
										</div>
									</div>
								</div>
							</div>
						) : (
							''
						)}
						<p className="has-text-centered">
							<button
								type="submit"
								disabled={isSubmitting}
								className={classNames(
									isSubmitting && `is-loading`,
									`button is-warning is-medium`
								)}
							>
								Submit
							</button>
						</p>
						<div
							className={status === 'success' ? `is-active modal` : `modal`}
							id="form-success"
						>
							<div className="modal-background" onClick={() => setStatus('')}>
								<div className="before" />
								<div className="after" />
							</div>
							<div className="modal-content">
								<div className="box">
									<div className="content">
										<h1>We have received your enquiry 😄</h1>
										<p>
											Thanks for taking the time to book a free demo.
											<br />
											We will be in touch soon.
										</p>
									</div>
								</div>
							</div>
							<button
								className="modal-close is-large"
								aria-label="close"
								onClick={() => setStatus('')}
							/>
						</div>
						<div
							className={status === 'failure' ? `is-active modal` : `modal`}
							id="form-failure"
						>
							<div className="modal-background" onClick={() => setStatus('')} />
							<div className="modal-content">
								<div className="box">
									<div className="content">
										<h1>Something went wrong 😞</h1>
										<p>
											Form is invalid, please check your details and try again.
										</p>
									</div>
								</div>
							</div>
							<button
								className="modal-close is-large"
								aria-label="close"
								onClick={() => setStatus('')}
							/>
						</div>
					</form>
				)}
			/>
		)
	}
}

BookADemo.propTypes = {
	formName: PropTypes.string,
	modelOptions: PropTypes.array,
	regionOptions: PropTypes.array,
	conditionOptions: PropTypes.array,
}

export default BookADemo
